import React, { useState, useEffect, useRef } from 'react';
import Modal from '../components/Layout/Modal';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import ElevatedButton from '../components/Layout/ElevatedButton';
import { FaBookOpen, FaCheckCircle, FaBook, FaChartBar } from 'react-icons/fa';
import PMSCourseManagement from '../images/PMSCourseManagement.png';
import PMSAssignmentTracking from '../images/PMSAssignmentTracking.png';
import PMSFlashcards from '../images/PMSFlashcards.png';
import PMSAnalytics from '../images/PMSAnalytics.png';
import PMSStudyTracker from '../images/PMSStudyTracker.png';
import PMSSmartCalendar from '../images/PMSSmartCalendar.png';

const Header = styled.div`
  min-height: 100vh;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  position: relative;
  z-index: 1;
  padding: 6rem 2rem 8rem 2rem;

  @media (max-width: 768px) {
    padding: 4rem 1rem 4rem 1rem;
  }

  @media (max-width: 480px) {
    padding: 3rem 1rem 3rem 1rem;
  }
`;

const HeroContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const QuickBenefits = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  max-width: 1100px;
  width: 100%;
  margin-top: 1.5rem;
  padding: 0 1rem;
  justify-items: center;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.6rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(1, 1fr);
    gap: 0.75rem;
    padding: 0 1rem;
  }
`;

/* Hero stats removed per UX preference; restored original quick presentation */

const BenefitCard = styled.div`
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 12px;
  padding: 0.85rem 0.9rem;
  text-align: center;
  color: ${props => props.theme.text};
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  width: 100%;
  max-width: 240px;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 10px 30px rgba(16,24,40,0.08);
  }
`;

const BenefitIcon = styled.div`
  height: 48px;
  width: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 0.75rem auto;
  background: ${props => `${props.theme.primary}10`};
  color: ${props => props.theme.primary};
  font-size: 1.1rem;
`;

const BenefitLabel = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.25;

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;


const WelcomeText = styled.h1`
  color: ${props => props.theme.text};
  margin: 0 0 0.75rem 0;
  font-weight: 800;
  font-size: 3.2rem;
  letter-spacing: -0.02em;
  line-height: 1.05;

  @media (max-width: 768px) {
    font-size: 2.4rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const SubText = styled.p`
  color: ${props => props.theme.textMuted};
  margin: 0;
  font-size: 1.5rem;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.125rem;
    max-width: 90%;
    padding: 0 1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const Subtitle = styled.h2`
  color: ${props => props.theme.buttonPrimary};
  margin: 0.5rem 0 1.5rem 0;
  font-weight: 500;
  font-size: 2rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 3rem;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    padding: 0 1rem;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

const FeaturesSection = styled.div`
  margin-bottom: 2rem;
  position: relative;
  z-index: 1;
  padding: 2rem 1rem;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid ${props => props.theme.border};
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.text};
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
`;

/* Removed older feature grid styles (replaced by PictureShowcase) */

const CTASection = styled.div`
  background: ${props => props.theme.cardBackground};
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  margin-bottom: 2rem;
  border: 1px solid ${props => props.theme.border};
  position: relative;
  z-index: 1;
  margin: 2rem 1rem 2rem 1rem;

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
    margin: 1rem 0.5rem;
  }
`;

const CTATitle = styled.h3`
  color: ${props => props.theme.text};
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0 0 1rem 0;

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const CTADescription = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 1.125rem;
  margin: 0 0 2rem 0;
  line-height: 1.6;
  max-width: 700px;
  margin: 0 auto 2rem auto;

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;


// Commented out PlatformStats components - can be uncommented when platform stats feature is implemented
/*
const PlatformStatsSection = styled.div`
  margin-bottom: 2rem;
  position: relative;
  z-index: 1;
  padding: 2rem 1rem;
`;

const PlatformStatsHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid ${props => props.theme.border};
`;

const PlatformStatsTitle = styled.h3`
  color: ${props => props.theme.text};
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
`;

const PlatformStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 400px;
  }
`;

const PlatformStatCard = styled.div`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  transition: all 0.2s ease;
  text-align: center;

  &:hover {
    background: ${props => props.theme.backgroundSecondary};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16);
  }
`;

const PlatformStatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${props => props.color || props.theme.text};
  margin-bottom: 0.5rem;
  line-height: 1;
`;

const PlatformStatLabel = styled.div`
  color: ${props => props.theme.textMuted};
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;
*/

const MockAppContainer = styled.div`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  overflow: hidden;
  margin: 2rem 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const MockAppHeader = styled.div`
  background: ${props => props.theme.backgroundSecondary};
  padding: 1rem 1.5rem;
  border-bottom: 1px solid ${props => props.theme.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MockAppTitle = styled.div`
  color: ${props => props.theme.text};
  font-size: 1.125rem;
  font-weight: 600;
`;

const MockAppContent = styled.div`
  display: flex;
  height: 500px;

  @media (max-width: 768px) {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const MockSidebar = styled.div`
  width: 280px;
  background: ${props => props.theme.backgroundSecondary};
  border-right: 1px solid ${props => props.theme.border};
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  @media (max-width: 768px) {
    min-width: 280px;
  }
`;

const MockSidebarSection = styled.div`
  margin-bottom: 1.5rem;
`;

const MockSidebarTitle = styled.div`
  color: ${props => props.theme.text};
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MockAssignmentItem = styled.div`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.backgroundSecondary};
  }
`;

const MockAssignmentTitle = styled.div`
  color: ${props => props.theme.text};
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const MockAssignmentMeta = styled.div`
  color: ${props => props.theme.textMuted};
  font-size: 0.75rem;
  display: flex;
  justify-content: space-between;
`;

const MockMainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const MockDashboardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.background};
`;

const MockDashboardTitle = styled.h3`
  color: ${props => props.theme.text};
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

const MockStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
`;

const MockStatCard = styled.div`
  background: ${props => props.theme.backgroundSecondary};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  padding: 1rem;
  text-align: center;
`;

const MockStatNumber = styled.div`
  color: ${props => props.color || props.theme.text};
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
`;

const MockStatLabel = styled.div`
  color: ${props => props.theme.textMuted};
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MockCalendarSection = styled.div`
  flex: 1;
  padding: 1.5rem;
  display: flex;
  gap: 1.5rem;
`;

const MockCalendarContainer = styled.div`
  flex: 1;
  background: ${props => props.theme.backgroundSecondary};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  padding: 1rem;
`;

const MockCalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const MockCalendarTitle = styled.h4`
  color: ${props => props.theme.text};
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
`;

const MockCalendarNav = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const MockCalendarButton = styled.button`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  color: ${props => props.theme.text};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;

  &:hover {
    background: ${props => props.theme.backgroundSecondary};
  }
`;

const MockCalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

const MockCalendarDayHeader = styled.div`
  color: ${props => props.theme.textMuted};
  font-size: 0.75rem;
  text-align: center;
  padding: 0.5rem;
  font-weight: 600;
`;

const MockCalendarDay = styled.div`
  background: ${props => props.today ? '#5865f2' : props.hasEvent ? props.theme.background : props.theme.backgroundSecondary};
  border: 1px solid ${props => props.theme.border};
  min-height: 60px;
  padding: 0.25rem;
  position: relative;
`;

const MockCalendarDayNumber = styled.div`
  color: ${props => props.today ? props.theme.text : props.theme.textMuted};
  font-size: 0.75rem;
  font-weight: ${props => props.today ? '600' : '400'};
  margin-bottom: 0.25rem;
`;

const MockCalendarEvent = styled.div`
  background: ${props => props.color || '#5865f2'};
  color: ${props => props.theme.text};
  font-size: 0.625rem;
  padding: 0.125rem 0.25rem;
  border-radius: 2px;
  margin-bottom: 0.125rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MockUpcomingSidebar = styled.div`
  width: 250px;
  background: ${props => props.theme.backgroundSecondary};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  padding: 1rem;
`;

const MockUpcomingTitle = styled.h4`
  color: ${props => props.theme.text};
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
`;

const MockUpcomingItem = styled.div`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
`;

const MockUpcomingItemTitle = styled.div`
  color: ${props => props.theme.text};
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const MockUpcomingItemMeta = styled.div`
  color: ${props => props.theme.textMuted};
  font-size: 0.75rem;
  display: flex;
  justify-content: space-between;
`;

/* Picture-format feature showcase (accepts images later) */
const PictureShowcase = styled.section`
  width: 100%;
  max-width: 1200px;
  margin: 2.5rem auto;
  padding: 0 1rem;
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

const PictureItem = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const PictureMedia = styled.div`
  border-radius: 12px;
  overflow: hidden;
  min-height: 0;
  background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.08));
  border: 1px solid ${props => props.theme.border};
  box-shadow: 0 14px 40px rgba(2,6,23,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.textSecondary};
  font-weight: 700;
  font-size: 1rem;
  cursor: zoom-in;

  @media (max-width: 480px) {
    min-height: 220px;
  }
  @media (max-width: 900px) {
    /* On small screens make image appear above the text regardless of DOM order */
    order: -1;
  }
`;

const PictureImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
`;

const PreviewBox = styled.div`
  position: fixed;
  z-index: 1200;
  max-width: 90vw;
  max-height: 85vh;
  width: min(1100px, 80vw);
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 10px;
  box-shadow: 0 18px 60px rgba(2,6,23,0.6);
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
`;

const PreviewImg = styled.img`
  max-width: 100%;
  max-height: 82vh;
  object-fit: contain;
  display: block;
`;

const PictureContent = styled.div`
  color: ${props => props.theme.text};
`;

const PictureTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1.75rem;
  font-weight: 800;
`;

const PictureText = styled.p`
  margin: 0 0 1rem 0;
  color: ${props => props.theme.textMuted};
  line-height: 1.6;
`;

const PictureList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  color: ${props => props.theme.textMuted};
`;

const PictureLi = styled.li`
  margin-bottom: 0.5rem;
`;

/* FeatureShowcase removed per user request */

const InformationalBanner = styled.div`
  width: auto;
  margin: 2rem auto;
  background: linear-gradient(90deg, #ffe5e5 0%, #ffebf0 100%);
  border: 1px solid #ffb6c1;
  color: #923b4c;
  padding: 1rem 1.25rem;
  margin-top: calc(1.25rem + 3rem);
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 5.5rem;
  z-index: 10;
`;

const BannerText = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.4;
  max-width: 78%;
`;

const BannerLink = styled.button`
  color: #8f2e4c;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.95rem;
  margin-left: 1rem;
  background: transparent;
  border: none;
  padding: 0;

  &:hover {
    color: #6c1f39;
  }
`;

const DisclaimerText = styled.p`
  color: ${props => props.theme.text};
  line-height: 1.5;
  margin-bottom: 1rem;
`;


const Home = () => {
  const featuresRef = useRef(null);
  const [isInformationalOpen, setInformationalOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSrc, setModalSrc] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [previewPos, setPreviewPos] = useState({ x: 0, y: 0 });
  const [isHoveringImage, setIsHoveringImage] = useState(false);
  const [isHoveringPreview, setIsHoveringPreview] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  const previewRef = useRef(null);
  const [previewStyle, setPreviewStyle] = useState({ left: '50%', top: '50%', transform: 'translate(-50%,-50%)' });

  // Clear bypass flag when Home component mounts (user has arrived at homepage)
  useEffect(() => {
    sessionStorage.removeItem('bypassDashboardRedirect');
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Recalculate preview position to clamp inside viewport whenever mouse moves, preview changes, or resize
  useEffect(() => {
    if (!previewSrc || !previewRef.current || typeof window === 'undefined') return;

    let raf = null;
    const recalc = () => {
      if (!previewRef.current) return;
      let box;
      try {
        box = previewRef.current.getBoundingClientRect();
      } catch (err) {
        return;
      }
      const padding = 8; // small gap from edges
      let left = previewPos.x + 18;
      let top = previewPos.y + 18;

      // Clamp horizontally
      if (left + box.width + padding > window.innerWidth) {
        left = window.innerWidth - box.width - padding;
      }
      if (left < padding) left = padding;

      // Clamp vertically
      if (top + box.height + padding > window.innerHeight) {
        top = window.innerHeight - box.height - padding;
      }
      if (top < padding) top = padding;

      setPreviewStyle({ left: left + 'px', top: top + 'px', transform: 'none' });
    };

    // Recalc once immediately
    raf = requestAnimationFrame(recalc);

    const handleResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(recalc);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', handleResize);
    };
  }, [previewPos, previewSrc]);

  // hide preview when neither hovering image nor preview, or on mobile
  const previewVisible = !isMobile && previewSrc && (isHoveringImage || isHoveringPreview);

  useEffect(() => {
    if (!previewVisible) {
      setPreviewSrc(null);
    }
  }, [previewVisible]);

  return (
    <>
      <Header>
        <InformationalBanner>
          <BannerText>
            Latest Changes: Flashcards
          </BannerText>
          <BannerLink onClick={() => setInformationalOpen(true)}>
            View more
          </BannerLink>
        </InformationalBanner>
        <HeroContent>
          <WelcomeText>PlanMySemester</WelcomeText>
          <Subtitle>Built by a Student, For Students</Subtitle>
          <SubText>
            Master your academic journey with intelligent planning tools and study assistance.
            Organize courses, track assignments, and get personalized support.
          </SubText>
          
          <HeroButtons>
            <StyledLink to="/register">
              <ElevatedButton primary>Get Started Free</ElevatedButton>
            </StyledLink>
            <StyledLink to="/login">
              <ElevatedButton>Sign In</ElevatedButton>
            </StyledLink>
          </HeroButtons>
        </HeroContent>

        <QuickBenefits>
          <BenefitCard borderColor="#9c27b0">
            <BenefitIcon><FaBookOpen /></BenefitIcon>
            <BenefitLabel>Course Management</BenefitLabel>
          </BenefitCard>
          <BenefitCard borderColor="#28a745">
            <BenefitIcon><FaCheckCircle /></BenefitIcon>
            <BenefitLabel>Assignment Tracking</BenefitLabel>
          </BenefitCard>
          <BenefitCard borderColor="#17a2b8">
            <BenefitIcon><FaBook /></BenefitIcon>
            <BenefitLabel>Flashcards</BenefitLabel>
          </BenefitCard>
          <BenefitCard borderColor="#ffc107">
            <BenefitIcon><FaChartBar /></BenefitIcon>
            <BenefitLabel>Study Tracker</BenefitLabel>
          </BenefitCard>
        </QuickBenefits>
      </Header>

      <FeaturesSection ref={featuresRef}>
        <SectionHeader>
          <SectionTitle>Complete Academic Management Suite</SectionTitle>
        </SectionHeader>
        <PictureShowcase>
          <PictureItem>
            <PictureMedia
              role="button"
              tabIndex={0}
              onMouseEnter={() => { setIsHoveringImage(true); setPreviewSrc(PMSCourseManagement); }}
              onMouseMove={(e) => { setPreviewPos({ x: e.clientX + 18, y: e.clientY + 18 }); }}
              onMouseLeave={() => setIsHoveringImage(false)}
              onClick={() => { setModalSrc(PMSCourseManagement); setModalTitle('Course Management'); setModalOpen(true); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setModalSrc(PMSCourseManagement); setModalTitle('Course Management'); setModalOpen(true); } }}
            >
              <PictureImg src={PMSCourseManagement} alt="Course Management preview" loading="lazy" />
            </PictureMedia>
            <PictureContent>
              <PictureTitle>Course Management</PictureTitle>
              <PictureText>Organize all your courses with detailed information and color-coded identification.</PictureText>
              <PictureList>
                <PictureLi>Add/edit/delete courses with ease</PictureLi>
                <PictureLi>Course codes and full names</PictureLi>
                <PictureLi>Grade tracking and management</PictureLi>
                <PictureLi>Color-coded course badges</PictureLi>
              </PictureList>
            </PictureContent>
          </PictureItem>

          <PictureItem>
            <PictureContent>
              <PictureTitle>Assignment Tracking</PictureTitle>
              <PictureText>Never miss a deadline with detailed assignment management and status tracking.</PictureText>
              <PictureList>
                <PictureLi>Create assignments with due dates</PictureLi>
                <PictureLi>Status tracking (pending, in-progress, completed, overdue)</PictureLi>
                <PictureLi>Course association and color inheritance</PictureLi>
                <PictureLi>Alphabetical sorting and filtering</PictureLi>
              </PictureList>
            </PictureContent>
            <PictureMedia
              role="button"
              tabIndex={0}
              onMouseEnter={() => { setIsHoveringImage(true); setPreviewSrc(PMSAssignmentTracking); }}
              onMouseMove={(e) => { setPreviewPos({ x: e.clientX + 18, y: e.clientY + 18 }); }}
              onMouseLeave={() => setIsHoveringImage(false)}
              onClick={() => { setModalSrc(PMSAssignmentTracking); setModalTitle('Assignment Tracking'); setModalOpen(true); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setModalSrc(PMSAssignmentTracking); setModalTitle('Assignment Tracking'); setModalOpen(true); } }}
            >
              <PictureImg src={PMSAssignmentTracking} alt="Assignment Tracking preview" loading="lazy" />
            </PictureMedia>
          </PictureItem>

          <PictureItem>
            <PictureMedia
              role="button"
              tabIndex={0}
              onMouseEnter={() => { setIsHoveringImage(true); setPreviewSrc(PMSFlashcards); }}
              onMouseMove={(e) => { setPreviewPos({ x: e.clientX + 18, y: e.clientY + 18 }); }}
              onMouseLeave={() => setIsHoveringImage(false)}
              onClick={() => { setModalSrc(PMSFlashcards); setModalTitle('Flashcards'); setModalOpen(true); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setModalSrc(PMSFlashcards); setModalTitle('Flashcards'); setModalOpen(true); } }}
            >
              <PictureImg src={PMSFlashcards} alt="Flashcards preview" loading="lazy" />
            </PictureMedia>
            <PictureContent>
              <PictureTitle>Flashcards</PictureTitle>
              <PictureText>Create and manage flashcards for effective studying and memorization.</PictureText>
              <PictureList>
                <PictureLi>Create flashcards with questions and answers</PictureLi>
                <PictureLi>Progress tracking and performance analytics</PictureLi>
                <PictureLi>Customizable card sets and tag them to courses</PictureLi>
                <PictureLi>Import flashcards from external sources (limited format support)</PictureLi>
              </PictureList>
            </PictureContent>
          </PictureItem>

          <PictureItem>
            <PictureContent>
              <PictureTitle>Analytics & Progress Tracking</PictureTitle>
              <PictureText>Track your academic performance with detailed analytics and progress insights.</PictureText>
              <PictureList>
                <PictureLi>Assignment completion statistics</PictureLi>
                <PictureLi>Course grade tracking</PictureLi>
                <PictureLi>Visual progress indicators</PictureLi>
                <PictureLi>Performance trends over time</PictureLi>
              </PictureList>
            </PictureContent>
            <PictureMedia
              role="button"
              tabIndex={0}
              onMouseEnter={() => { setIsHoveringImage(true); setPreviewSrc(PMSAnalytics); }}
              onMouseMove={(e) => { setPreviewPos({ x: e.clientX + 18, y: e.clientY + 18 }); }}
              onMouseLeave={() => setIsHoveringImage(false)}
              onClick={() => { setModalSrc(PMSAnalytics); setModalTitle('Analytics & Progress Tracking'); setModalOpen(true); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setModalSrc(PMSAnalytics); setModalTitle('Analytics & Progress Tracking'); setModalOpen(true); } }}
            >
              <PictureImg src={PMSAnalytics} alt="Analytics preview" loading="lazy" />
            </PictureMedia>
          </PictureItem>

          <PictureItem>
            <PictureMedia
              role="button"
              tabIndex={0}
              onMouseEnter={() => { setIsHoveringImage(true); setPreviewSrc(PMSStudyTracker); }}
              onMouseMove={(e) => { setPreviewPos({ x: e.clientX + 18, y: e.clientY + 18 }); }}
              onMouseLeave={() => setIsHoveringImage(false)}
              onClick={() => { setModalSrc(PMSStudyTracker); setModalTitle('Study Tracker'); setModalOpen(true); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setModalSrc(PMSStudyTracker); setModalTitle('Study Tracker'); setModalOpen(true); } }}
            >
              <PictureImg src={PMSStudyTracker} alt="Study Tracker preview" loading="lazy" />
            </PictureMedia>
            <PictureContent>
              <PictureTitle>Built-in Study Tracker</PictureTitle>
              <PictureText>Track your study sessions and productivity with our integrated study tracker, designed to promote study habits and time management.</PictureText>
              <PictureList>
                <PictureLi>Earn study points for each session</PictureLi>
                <PictureLi>Earn badges for study milestones</PictureLi>
                <PictureLi>Earn streaks for consecutive study days</PictureLi>
                <PictureLi>Earn skip days for consistent study habits</PictureLi>
              </PictureList>
            </PictureContent>
          </PictureItem>

          <PictureItem>
            <PictureContent>
              <PictureTitle>Smart Calendar</PictureTitle>
              <PictureText>Integrate your assignments and study sessions into a smart calendar for better time management and planning.</PictureText>
              <PictureList>
                <PictureLi>Sync assignments with your calendar</PictureLi>
                <PictureLi>Set reminders and notifications</PictureLi>
                <PictureLi>Visualize your schedule and workload</PictureLi>
                <PictureLi>Adjust study sessions based on priority and deadlines</PictureLi>
              </PictureList>
            </PictureContent>
            <PictureMedia
              role="button"
              tabIndex={0}
              onMouseEnter={() => { setIsHoveringImage(true); setPreviewSrc(PMSSmartCalendar); }}
              onMouseMove={(e) => { setPreviewPos({ x: e.clientX + 18, y: e.clientY + 18 }); }}
              onMouseLeave={() => setIsHoveringImage(false)}
              onClick={() => { setModalSrc(PMSSmartCalendar); setModalTitle('Smart Calendar'); setModalOpen(true); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setModalSrc(PMSSmartCalendar); setModalTitle('Smart Calendar'); setModalOpen(true); } }}
            >
              <PictureImg src={PMSSmartCalendar} alt="Smart Calendar preview" loading="lazy" />
            </PictureMedia>
          </PictureItem>
        </PictureShowcase>

        {/* Hover preview box (non-interactive) */}
        {previewSrc && previewVisible && (
          <PreviewBox
            ref={previewRef}
            aria-hidden
            onMouseEnter={() => setIsHoveringPreview(true)}
            onMouseLeave={() => setIsHoveringPreview(false)}
            style={previewStyle}
          >
            <PreviewImg src={previewSrc} alt="Preview" />
          </PreviewBox>
        )}

        {/* Click-to-open modal for full view */}
        <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={modalTitle} size="large">
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <img src={modalSrc} alt={modalTitle} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
          </div>
        </Modal>
      </FeaturesSection>

      <MockAppContainer>
        <MockAppHeader>
          <MockAppTitle>PlanMySemester Dashboard Preview</MockAppTitle>
          <div style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>See what your academic dashboard looks like</div>
        </MockAppHeader>
        <MockAppContent>
          <MockSidebar>
            <MockSidebarSection>
              <MockSidebarTitle>Upcoming Assignments</MockSidebarTitle>
              <MockAssignmentItem>
                <MockAssignmentTitle>Calculus II Final Exam</MockAssignmentTitle>
                <MockAssignmentMeta>
                  <span>MATH 202</span>
                  <span style={{ color: 'var(--color-error)' }}>Due: Dec 15</span>
                </MockAssignmentMeta>
              </MockAssignmentItem>
              <MockAssignmentItem>
                <MockAssignmentTitle>Database Design Project</MockAssignmentTitle>
                <MockAssignmentMeta>
                  <span>CS 301</span>
                  <span style={{ color: 'var(--color-warning)' }}>Due: Dec 18</span>
                </MockAssignmentMeta>
              </MockAssignmentItem>
              <MockAssignmentItem>
                <MockAssignmentTitle>Research Paper Draft</MockAssignmentTitle>
                <MockAssignmentMeta>
                  <span>ENG 401</span>
                  <span style={{ color: 'var(--color-success)' }}>Due: Dec 20</span>
                </MockAssignmentMeta>
              </MockAssignmentItem>
            </MockSidebarSection>

            <MockSidebarSection>
              <MockSidebarTitle>Quick Stats</MockSidebarTitle>
              <MockStatCard>
                <MockStatNumber color="#28a745">24</MockStatNumber>
                <MockStatLabel>Total Assignments</MockStatLabel>
              </MockStatCard>
              <MockStatCard>
                <MockStatNumber color="#5865f2">6</MockStatNumber>
                <MockStatLabel>Active Courses</MockStatLabel>
              </MockStatCard>
              <MockStatCard>
                <MockStatNumber color="#ffc107">3.7</MockStatNumber>
                <MockStatLabel>Current GPA</MockStatLabel>
              </MockStatCard>
            </MockSidebarSection>
          </MockSidebar>

          <MockMainContent>
            <MockDashboardHeader>
              <MockDashboardTitle>Dashboard Overview</MockDashboardTitle>
              <MockStatsGrid>
                <MockStatCard>
                  <MockStatNumber color="#28a745">18</MockStatNumber>
                  <MockStatLabel>Completed</MockStatLabel>
                </MockStatCard>
                <MockStatCard>
                  <MockStatNumber color="#ffc107">4</MockStatNumber>
                  <MockStatLabel>In Progress</MockStatLabel>
                </MockStatCard>
                <MockStatCard>
                  <MockStatNumber color="#dc3545">2</MockStatNumber>
                  <MockStatLabel>Overdue</MockStatLabel>
                </MockStatCard>
                <MockStatCard>
                  <MockStatNumber color="#5865f2">85%</MockStatNumber>
                  <MockStatLabel>Completion Rate</MockStatLabel>
                </MockStatCard>
              </MockStatsGrid>
            </MockDashboardHeader>

            <MockCalendarSection>
              <MockCalendarContainer>
                <MockCalendarHeader>
                  <MockCalendarTitle>December 2025</MockCalendarTitle>
                  <MockCalendarNav>
                    <MockCalendarButton>‹</MockCalendarButton>
                    <MockCalendarButton>›</MockCalendarButton>
                  </MockCalendarNav>
                </MockCalendarHeader>
                <MockCalendarGrid>
                  <MockCalendarDayHeader>Sun</MockCalendarDayHeader>
                  <MockCalendarDayHeader>Mon</MockCalendarDayHeader>
                  <MockCalendarDayHeader>Tue</MockCalendarDayHeader>
                  <MockCalendarDayHeader>Wed</MockCalendarDayHeader>
                  <MockCalendarDayHeader>Thu</MockCalendarDayHeader>
                  <MockCalendarDayHeader>Fri</MockCalendarDayHeader>
                  <MockCalendarDayHeader>Sat</MockCalendarDayHeader>

                  {/* Calendar days - simplified mock */}
                  {Array.from({ length: 35 }, (_, i) => {
                    const day = i - 2; // Offset to start on correct day
                    const isToday = day === 11; // December 11th
                    const hasEvent = [8, 15, 18, 22, 25].includes(day); // Mock events
                    return (
                      <MockCalendarDay key={i} today={isToday} hasEvent={hasEvent}>
                        <MockCalendarDayNumber today={isToday}>
                          {day > 0 && day <= 31 ? day : ''}
                        </MockCalendarDayNumber>
                        {hasEvent && day === 15 && (
                          <MockCalendarEvent color="#dc3545">Final Exam</MockCalendarEvent>
                        )}
                        {hasEvent && day === 18 && (
                          <MockCalendarEvent color="#ffc107">Project Due</MockCalendarEvent>
                        )}
                        {hasEvent && day === 22 && (
                          <MockCalendarEvent color="#28a745">Paper Due</MockCalendarEvent>
                        )}
                      </MockCalendarDay>
                    );
                  })}
                </MockCalendarGrid>
              </MockCalendarContainer>

              <MockUpcomingSidebar>
                <MockUpcomingTitle>Upcoming This Week</MockUpcomingTitle>
                <MockUpcomingItem>
                  <MockUpcomingItemTitle>Database Systems Quiz</MockUpcomingItemTitle>
                  <MockUpcomingItemMeta>
                    <span>CS 301</span>
                    <span>Tomorrow</span>
                  </MockUpcomingItemMeta>
                </MockUpcomingItem>
                <MockUpcomingItem>
                  <MockUpcomingItemTitle>Literature Review</MockUpcomingItemTitle>
                  <MockUpcomingItemMeta>
                    <span>ENG 401</span>
                    <span>Dec 13</span>
                  </MockUpcomingItemMeta>
                </MockUpcomingItem>
                <MockUpcomingItem>
                  <MockUpcomingItemTitle>Math Problem Set</MockUpcomingItemTitle>
                  <MockUpcomingItemMeta>
                    <span>MATH 202</span>
                    <span>Dec 14</span>
                  </MockUpcomingItemMeta>
                </MockUpcomingItem>
              </MockUpcomingSidebar>
            </MockCalendarSection>
          </MockMainContent>
        </MockAppContent>
      </MockAppContainer>

      

      {/* Platform stats section - commented out until feature is implemented */}
      {/*
      {platformStats.totalUsers > 0 && platformStats.totalCourses > 0 && platformStats.totalAssignments > 0 && (
        <PlatformStatsSection>
        <PlatformStatsHeader>
          <PlatformStatsTitle>Community Statistics</PlatformStatsTitle>
        </PlatformStatsHeader>
        <PlatformStatsGrid>
          <PlatformStatCard borderColor="#28a745">
            <PlatformStatNumber color="#28a745">
              {platformStats.loading ? '...' : platformStats.totalUsers.toLocaleString()}
            </PlatformStatNumber>
            <PlatformStatLabel>Active Users</PlatformStatLabel>
          </PlatformStatCard>
          <PlatformStatCard borderColor="#9c27b0">
            <PlatformStatNumber color="#9c27b0">
              {platformStats.loading ? '...' : platformStats.totalCourses.toLocaleString()}
            </PlatformStatNumber>
            <PlatformStatLabel>Total Courses Created</PlatformStatLabel>
          </PlatformStatCard>
          <PlatformStatCard borderColor="#5865f2">
            <PlatformStatNumber color="#5865f2">
              {platformStats.loading ? '...' : platformStats.totalAssignments.toLocaleString()}
            </PlatformStatNumber>
            <PlatformStatLabel>Total Assignments Created</PlatformStatLabel>
          </PlatformStatCard>
        </PlatformStatsGrid>
      </PlatformStatsSection>
      )}
      */}

      <Modal
        isOpen={isInformationalOpen}
        onClose={() => setInformationalOpen(false)}
        title="New Feature Announcement: Flashcards"
        size="medium"
      >
        <DisclaimerText>
          Recently, we have made the difficult decision to, potentially temporarily, remove AI powered features from PlanMySemester. However, we have been hard at work in
          developing new features and improvements to enhance your academic planning experience, and we are excited to share some of the upcoming additions we have in store for you:
        </DisclaimerText>
        <DisclaimerText>
          Flashcards: We have just introduced a powerful flashcard feature that allows you to create and manage flashcards for effective studying and memorization. You can track your 
          progress, analyze performance, and customize card sets to enhance your learning experience. Furthermore, we are actively working on expanding the flashcard feature to include
          more advanced import options and enhancements to make it even more versatile and useful for your study needs.
        </DisclaimerText>
        <hr style={{ border: `1px solid ${props => props.theme.border}`, margin: '1.5rem 0' }} />
        <DisclaimerText>
          <i>Thank you for your support and understanding! If you have any thoughts, suggestions, or questions, feel free to reach out to our support team. <br />If you run into
          any issues regarding the new introduction of flashcards, please don't hesitate to contact us.</i>
        </DisclaimerText>
      </Modal>

      <CTASection>
        <CTATitle>Ready to Transform Your Academic Experience?</CTATitle>
        <CTADescription>
          Take control of your academic success with PlanMySemester. Join students who want to organize their academic lives,
          stay on top of deadlines, and achieve their academic goals with our vast planning tools and assistance.
        </CTADescription>
        <StyledLink to="/register">
          <ElevatedButton primary large>Start Your Academic Journey</ElevatedButton>
        </StyledLink>
      </CTASection>

    </>
  );
};



export default Home;
